import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Sparkles,
  Loader2,
  Search,
  CirclePause,
  Clock3,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  Megaphone,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { cn } from '@/lib/utils'

import {
  companiesApi,
  ListRunCompaniesPage,
  ListRunCompanyMatchStatus,
  ListRunCompanyRow,
  ListRunDetail,
  ListRunSourceSummary,
  ListRunStatus,
  ListRunSummary,
  listsApi,
} from '@/services/api'
import { SelectCampaignDialog } from '@/components/select-campaign-dialog'
import { BulkPipelineToolbar, BulkAddToCampaignButton } from '@/components/bulk-pipeline-toolbar'
import { Checkbox } from '@/components/ui/checkbox'
import {
  PIPELINE_STAGES,
  PipelineStage,
  STAGE_CONFIG,
} from '@/components/pipeline-stage-badge'
import { extractErrorMessage } from '@/lib/errors'
import { useOrgStore } from '@/stores/org-store'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const FALLBACK_DEFAULT_QUERY =
  'Find small to medium size web3 companies, related to game development.'

const ACTIVE_STATUSES: ListRunStatus[] = [
  'queued',
  'enhancing_query',
  'discovering_sources',
  'running_sources',
]

const STATUS_LABELS: Record<ListRunStatus, string> = {
  queued: 'Queued',
  enhancing_query: 'Enhancing Query',
  discovering_sources: 'Discovering Sources',
  running_sources: 'Running Sources',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

const BUSINESS_TYPE_PLACEHOLDERS = new Set(['available services', 'services'])

function isPlaceholderBusinessType(value: string): boolean {
  const lower = value.toLowerCase().trim()
  if (!lower) return true
  if (BUSINESS_TYPE_PLACEHOLDERS.has(lower)) return true
  if (lower.startsWith('available services')) return true
  if (lower === 'other') return true
  return false
}

function isActiveStatus(status: ListRunStatus): boolean {
  return ACTIVE_STATUSES.includes(status)
}

function isListRunStatus(value: unknown): value is ListRunStatus {
  return typeof value === 'string' && value in STATUS_LABELS
}

function getStatusIcon(status: ListRunStatus) {
  if (status === 'completed') return <CheckCircle2 className="h-4 w-4" />
  if (status === 'failed') return <XCircle className="h-4 w-4" />
  if (status === 'cancelled') return <CirclePause className="h-4 w-4" />
  if (status === 'running_sources' || status === 'discovering_sources' || status === 'enhancing_query') {
    return <Loader2 className="h-4 w-4 animate-spin" />
  }
  return <Clock3 className="h-4 w-4" />
}

function getStatusVariant(
  status: ListRunStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'completed') return 'default'
  if (status === 'failed') return 'destructive'
  if (status === 'cancelled') return 'outline'
  return 'secondary'
}

function getCompanyBadgeStyle(status: ListRunCompanyMatchStatus): string {
  if (status === 'added_to_pipeline') {
    return 'bg-emerald-100 text-emerald-700 border-emerald-200'
  }
  return 'bg-slate-100 text-slate-700 border-slate-200'
}

function formatDate(value?: string | null): string {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

function parseCompanyRow(data: unknown): ListRunCompanyRow | null {
  if (!data || typeof data !== 'object') return null
  const row = data as Partial<ListRunCompanyRow>
  if (!row.id || !row.run_id || !row.raw_company_id || !row.match_status) {
    return null
  }
  return row as ListRunCompanyRow
}

function getSourceRowState(source: ListRunSourceSummary): string | null {
  const summary = source.result_summary
  if (!summary || typeof summary !== 'object') return null
  const state = (summary as Record<string, unknown>).state
  return typeof state === 'string' ? state : null
}

function getSourceSkipReasonLabel(source: ListRunSourceSummary): string | null {
  const summary = source.result_summary
  if (!summary || typeof summary !== 'object') return null
  const skipReason = (summary as Record<string, unknown>).skip_reason
  if (skipReason === 'missing_target_geolocation') {
    return 'No target geography set'
  }
  if (skipReason === 'region_only_match') {
    return 'Regional source (not country-specific)'
  }
  if (skipReason === 'no_target_country_match') {
    return 'No target-country match'
  }
  return null
}

interface SourceExecuteDialogContent {
  title: string
  description: string
}

function getSourceExecuteDialogContent(source: ListRunSourceSummary): SourceExecuteDialogContent {
  const summary = source.result_summary as Record<string, unknown> | null
  const skipReason = summary?.skip_reason as string | undefined
  const executionStatus = source.execution_status

  if (executionStatus === 'queued_suggested') {
    return {
      title: 'Run suggested source?',
      description:
        'This source was discovered during the search but was not executed in the original run. Running it will scrape the source and merge any new companies into this search result.',
    }
  }

  if (skipReason === 'missing_target_geolocation') {
    return {
      title: 'Run source outside target geography?',
      description:
        'This source was skipped because your organization has no target geography configured. Results may include companies from any country.',
    }
  }

  if (skipReason === 'region_only_match') {
    return {
      title: 'Run source outside target geography?',
      description:
        'This source covers a broad region but does not specifically target your countries. Results may include companies outside your target markets.',
    }
  }

  if (skipReason === 'no_target_country_match') {
    const targetCountries = summary?.target_countries as string[] | undefined
    const countryList = targetCountries?.join(', ') || 'your target countries'
    return {
      title: 'Run source outside target geography?',
      description: `This source does not cover ${countryList}. Results are unlikely to match your geographic focus.`,
    }
  }

  return {
    title: 'Run unverified source?',
    description:
      'This source was skipped because it could not be verified as ready for scraping. The scraper will attempt preparation before execution, but results are not guaranteed.',
  }
}

export default function ListsPage() {
  const { listId } = useParams<{ listId: string }>()
  const navigate = useNavigate()
  const currentOrg = useOrgStore((state) => state.currentOrg)
  const queryClient = useQueryClient()
  const selectedRunId = listId || null
  const isDetailRoute = Boolean(selectedRunId)

  const suggestedInitialQuery = useMemo(() => {
    const candidate = currentOrg?.sample_list_query?.trim()
    if (candidate) {
      return candidate
    }
    return FALLBACK_DEFAULT_QUERY
  }, [currentOrg?.sample_list_query])

  const [query, setQuery] = useState(suggestedInitialQuery)
  const [isQueryEdited, setIsQueryEdited] = useState(false)
  const [companyPage, setCompanyPage] = useState(1)
  const [companySearch, setCompanySearch] = useState('')
  const [companySourceFilter, setCompanySourceFilter] = useState<string>('all')
  const [activeCompanyStage, setActiveCompanyStage] = useState<PipelineStage | 'all'>('all')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isStreamConnected, setIsStreamConnected] = useState(false)
  const [streamWarning, setStreamWarning] = useState<string | null>(null)
  const [streamReconnectAttempts, setStreamReconnectAttempts] = useState(0)
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false)
  const [selectedCompanyForCampaign, setSelectedCompanyForCampaign] = useState<string | null>(null)
  const [excludedCompanies, setExcludedCompanies] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [sourceToExecute, setSourceToExecute] = useState<ListRunSourceSummary | null>(null)

  const excludeMutation = useMutation({
    mutationFn: (id: string) => companiesApi.exclude(id),
    onSuccess: (_, id) => {
      setExcludedCompanies((prev) => new Set(prev).add(id))
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const includeMutation = useMutation({
    mutationFn: (id: string) => companiesApi.include(id),
    onSuccess: (_, id) => {
      setExcludedCompanies((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const runsQuery = useQuery<ListRunSummary[]>({
    queryKey: ['lists', 'runs'],
    queryFn: () => listsApi.listRuns({ limit: 50 }),
    refetchInterval: (q) => {
      const rows = (q.state.data as ListRunSummary[] | undefined) || []
      if (!rows.some((run) => isActiveStatus(run.status))) {
        return 12000
      }
      return isStreamConnected ? 10000 : 3000
    },
  })

  const runs = runsQuery.data || []

  useEffect(() => {
    setCompanyPage(1)
  }, [selectedRunId])

  useEffect(() => {
    setCompanyPage(1)
  }, [companySearch, activeCompanyStage, companySourceFilter])

  useEffect(() => {
    if (selectedRunId) return
    setIsStreamConnected(false)
    setStreamWarning(null)
    setStreamReconnectAttempts(0)
  }, [selectedRunId])

  useEffect(() => {
    if (!isQueryEdited) {
      setQuery(suggestedInitialQuery)
    }
  }, [isQueryEdited, suggestedInitialQuery])

  const runDetailQuery = useQuery<ListRunDetail>({
    queryKey: ['lists', 'runs', selectedRunId],
    queryFn: () => listsApi.getRun(selectedRunId!),
    enabled: Boolean(selectedRunId),
    refetchInterval: (q) => {
      const run = q.state.data as ListRunDetail | undefined
      if (!run || !isActiveStatus(run.status)) return false
      return isStreamConnected ? 10000 : 3000
    },
  })

  const sourcesQuery = useQuery<{ source_name: string; count: number }[]>({
    queryKey: ['lists', 'runs', selectedRunId, 'companies', 'sources'],
    queryFn: () => listsApi.getRunCompanySources(selectedRunId!),
    enabled: Boolean(selectedRunId),
    staleTime: 30000,
  })

  const companySources = sourcesQuery.data || []

  const stageCountsQuery = useQuery<Record<string, number>>({
    queryKey: [
      'lists',
      'runs',
      selectedRunId,
      'companies',
      'stage-counts',
      companySearch,
      companySourceFilter,
    ],
    queryFn: () =>
      listsApi.getRunCompanyStageCounts(selectedRunId!, {
        search: companySearch || undefined,
        source:
          companySourceFilter && companySourceFilter !== 'all'
            ? companySourceFilter
            : undefined,
      }),
    enabled: Boolean(selectedRunId),
    staleTime: 30000,
  })

  const stageCounts = stageCountsQuery.data
  const stageCountsSum = stageCounts
    ? Object.values(stageCounts).reduce((a, b) => a + b, 0)
    : 0

  const companiesQuery = useQuery<ListRunCompaniesPage>({
    queryKey: [
      'lists',
      'runs',
      selectedRunId,
      'companies',
      companyPage,
      companySearch,
      activeCompanyStage,
      companySourceFilter,
    ],
    queryFn: () =>
      listsApi.listRunCompanies(selectedRunId!, {
        page: companyPage,
        per_page: 20,
        search: companySearch || undefined,
        stage: activeCompanyStage === 'all' ? undefined : activeCompanyStage,
        source:
          companySourceFilter && companySourceFilter !== 'all'
            ? companySourceFilter
            : undefined,
      }),
    enabled: Boolean(selectedRunId),
    refetchInterval: () => {
      const run = runDetailQuery.data as ListRunDetail | undefined
      if (!run) return false
      if (!isActiveStatus(run.status)) return false
      if (isStreamConnected && companyPage === 1) return 10000
      return 3000
    },
  })

  const selectedRunStatus = runDetailQuery.data?.status

  useEffect(() => {
    if (!selectedRunId) return
    if (!selectedRunStatus || !isActiveStatus(selectedRunStatus) || !companiesQuery.data) return

    const streamAbort = new AbortController()
    let stopped = false
    let afterId = companiesQuery.data.items.reduce((max, row) => Math.max(max, row.id), 0)
    let reconnectAttempts = 0

    setIsStreamConnected(false)
    setStreamWarning(null)
    setStreamReconnectAttempts(0)
    console.info('[ListsPage] Starting run event stream', {
      runId: selectedRunId,
      afterId,
    })

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, ms)
      })

    const upsertCompanyInCache = (incoming: ListRunCompanyRow) => {
      afterId = Math.max(afterId, incoming.id)
      queryClient.invalidateQueries({ queryKey: ['lists', 'runs', selectedRunId, 'companies'] })
      queryClient.invalidateQueries({
        queryKey: ['lists', 'runs', selectedRunId, 'companies', 'stage-counts'],
      })
      queryClient.invalidateQueries({
        queryKey: ['lists', 'runs', selectedRunId, 'companies', 'sources'],
      })
    }

    const updateRunStatusInCache = (status: ListRunStatus, completedAt?: string | null) => {
      queryClient.setQueryData<ListRunDetail>(['lists', 'runs', selectedRunId], (current) => {
        if (!current) return current
        return {
          ...current,
          status,
          completed_at: completedAt ?? current.completed_at,
        }
      })

      queryClient.setQueryData<ListRunSummary[]>(['lists', 'runs'], (current) => {
        if (!current) return current
        return current.map((run) =>
          run.id === selectedRunId
            ? {
                ...run,
                status,
                completed_at: completedAt ?? run.completed_at,
              }
            : run
        )
      })
    }

    const startStream = async () => {
      while (!stopped) {
        try {
          await listsApi.streamRunEvents(selectedRunId, {
            afterId,
            signal: streamAbort.signal,
            onEvent: ({ event, data }) => {
              if (event === 'connected') {
                setIsStreamConnected(true)
                setStreamWarning(null)
                setStreamReconnectAttempts(0)
                console.info('[ListsPage] Stream connected', { runId: selectedRunId })
                return
              }

              if (event === 'error') {
                setStreamWarning('Live updates had a transient backend error. Reconnecting automatically...')
                console.warn('[ListsPage] Stream event error', { runId: selectedRunId, data })
                return
              }

              if (event === 'company_matched') {
                const row = parseCompanyRow(data)
                if (row) {
                  upsertCompanyInCache(row)
                }
                return
              }

              if ((event === 'run_status' || event === 'run_completed') && data && typeof data === 'object') {
                const payload = data as { status?: unknown; completed_at?: unknown }
                if (isListRunStatus(payload.status)) {
                  updateRunStatusInCache(
                    payload.status,
                    typeof payload.completed_at === 'string' ? payload.completed_at : null
                  )
                }
                if (event === 'run_completed') {
                  setIsStreamConnected(false)
                  setStreamWarning(null)
                }
              }
            },
          })
          if (stopped || streamAbort.signal.aborted) {
            return
          }

          setIsStreamConnected(false)
          const latestRun = queryClient.getQueryData<ListRunDetail>(['lists', 'runs', selectedRunId])
          if (latestRun && !isActiveStatus(latestRun.status)) {
            return
          }
          reconnectAttempts += 1
          setStreamReconnectAttempts(reconnectAttempts)
          setStreamWarning('Live updates disconnected. Retrying stream...')
          console.warn('[ListsPage] Stream ended unexpectedly, retrying', {
            runId: selectedRunId,
            attempt: reconnectAttempts,
          })
          await wait(1200)
        } catch (error) {
          if (stopped || streamAbort.signal.aborted) {
            return
          }
          setIsStreamConnected(false)
          reconnectAttempts += 1
          setStreamReconnectAttempts(reconnectAttempts)
          setStreamWarning('Live updates disconnected. Retrying stream...')
          console.warn('[ListsPage] Stream request failed, retrying', {
            runId: selectedRunId,
            attempt: reconnectAttempts,
            error,
          })
          await wait(1200)
        }
      }
    }

    void startStream()

    return () => {
      stopped = true
      streamAbort.abort()
      console.info('[ListsPage] Stream stopped', { runId: selectedRunId })
    }
  }, [selectedRunId, selectedRunStatus, companiesQuery.isSuccess, queryClient])

  const createRunMutation = useMutation({
    mutationFn: ({ query, expandSearch = false }: { query: string; expandSearch?: boolean }) =>
      listsApi.createRun(query, { expand_search: expandSearch }),
    onSuccess: (created) => {
      setErrorMessage(null)
      navigate(`/app/lists/${created.id}`)
      queryClient.invalidateQueries({ queryKey: ['lists', 'runs'] })
      queryClient.invalidateQueries({ queryKey: ['lists', 'runs', created.id] })
      queryClient.invalidateQueries({ queryKey: ['lists', 'runs', created.id, 'companies'] })
    },
    onError: (err: unknown) => {
      const message = extractErrorMessage(err, 'Failed to start run')
      setErrorMessage(message)
    },
  })

  const cancelRunMutation = useMutation({
    mutationFn: (runId: string) => listsApi.cancelRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists', 'runs'] })
      if (selectedRunId) {
        queryClient.invalidateQueries({ queryKey: ['lists', 'runs', selectedRunId] })
      }
    },
  })

  const executeSourceMutation = useMutation({
    mutationFn: ({ runId, sourceRowId }: { runId: string; sourceRowId: number }) =>
      listsApi.executeRunSource(runId, sourceRowId),
    onMutate: async ({ sourceRowId }) => {
      if (!selectedRunId) return
      await queryClient.cancelQueries({ queryKey: ['lists', 'runs', selectedRunId] })
      const previousData = queryClient.getQueryData<ListRunDetail>(['lists', 'runs', selectedRunId])
      if (previousData) {
        queryClient.setQueryData<ListRunDetail>(['lists', 'runs', selectedRunId], {
          ...previousData,
          sources: previousData.sources.map((s) =>
            s.id === sourceRowId
              ? { ...s, result_summary: { ...(s.result_summary || {}), state: 'running' } }
              : s
          ),
        })
      }
      return { previousData }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData && selectedRunId) {
        queryClient.setQueryData(['lists', 'runs', selectedRunId], context.previousData)
      }
    },
    onSuccess: () => {
      if (selectedRunId) {
        queryClient.invalidateQueries({ queryKey: ['lists', 'runs', selectedRunId] })
        queryClient.invalidateQueries({ queryKey: ['lists', 'runs', selectedRunId, 'companies'] })
        queryClient.invalidateQueries({
          queryKey: ['lists', 'runs', selectedRunId, 'companies', 'sources'],
        })
        queryClient.invalidateQueries({
          queryKey: ['lists', 'runs', selectedRunId, 'companies', 'stage-counts'],
        })
      }
      setSourceToExecute(null)
    },
  })

  const selectedRun = runDetailQuery.data

  const sortedSources = useMemo(() => {
    const sources = selectedRun?.sources || []
    return [...sources].sort((a, b) => {
      const orderA = a.execution_order ?? 9999
      const orderB = b.execution_order ?? 9999
      if (orderA !== orderB) return orderA - orderB
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
      return timeB - timeA
    })
  }, [selectedRun])

  const geoSkippedSources = useMemo(
    () =>
      sortedSources.filter(
        (source) =>
          source.execution_status === 'skipped' && Boolean(getSourceSkipReasonLabel(source))
      ),
    [sortedSources]
  )

  const canExpandSearch = Boolean(
    selectedRun &&
      !isActiveStatus(selectedRun.status) &&
      geoSkippedSources.length > 0
  )

  const handleRun = () => {
    const trimmed = query.trim()
    if (!trimmed) {
      setErrorMessage('Query cannot be empty')
      return
    }
    createRunMutation.mutate({ query: trimmed })
  }

  const handleExpandSearch = () => {
    if (!selectedRun) return
    createRunMutation.mutate({
      query: selectedRun.raw_query,
      expandSearch: true,
    })
  }

  const companies = companiesQuery.data?.items || []
  const companyTotal = companiesQuery.data?.total || 0
  const totalCompanies =
    stageCountsSum > 0 ? stageCountsSum : companyTotal

  const allSelected =
    companies.length > 0 &&
    companies.every((c) => selectedIds.has(c.raw_company_id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(companies.map((c) => c.raw_company_id)))
    }
  }

  const toggleCompany = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  useEffect(() => {
    setSelectedIds(new Set())
  }, [selectedRunId, companyPage, companySearch, activeCompanyStage, companySourceFilter])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="flex items-center gap-2">
            {isDetailRoute && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/app/lists')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h1 className="text-2xl font-bold text-foreground">Smart Search</h1>
          </div>
          <p className="mt-1 text-muted-foreground">
            Run organization-scoped list searches with LLM-enhanced query execution.
          </p>
        </div>

        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Search className="h-4 w-4" />
              Tell Lev8 who matters. We'll find them.
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <Textarea
                value={query}
                onChange={(event) => {
                  setIsQueryEdited(true)
                  setQuery(event.target.value)
                }}
                placeholder={suggestedInitialQuery}
                rows={3}
                className="min-h-[80px] w-full flex-1 resize-y"
              />
              <Button
                onClick={handleRun}
                disabled={createRunMutation.isPending}
                className="sm:w-auto"
              >
                {createRunMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Run List Search
              </Button>
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Run History</h3>
                <Badge variant="outline">{runs.length}</Badge>
              </div>
              {runsQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading runs...
                </div>
              ) : runsQuery.isError ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {extractErrorMessage(runsQuery.error, 'Failed to load run history')}
                  </AlertDescription>
                </Alert>
              ) : runs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No runs yet. Run your first search above.</p>
              ) : (
                <div className="max-h-[200px] overflow-y-auto space-y-1.5">
                  {runs.map((run) => {
                    const selected = run.id === selectedRunId
                    return (
                      <button
                        key={run.id}
                        type="button"
                        onClick={() => navigate(`/app/lists/${run.id}`)}
                        className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors ${
                          selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                        }`}
                      >
                        <Badge variant={getStatusVariant(run.status)} className="shrink-0 gap-0.5 text-xs">
                          {getStatusIcon(run.status)}
                          {STATUS_LABELS[run.status]}
                        </Badge>
                        <span className="min-w-0 flex-1 truncate text-sm">{run.raw_query}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDate(run.created_at)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        {streamWarning && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {streamWarning}
              {streamReconnectAttempts > 0 ? ` (attempt ${streamReconnectAttempts})` : ''}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="space-y-6">
        {!selectedRunId ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Select a run from history above to view results.
              </CardContent>
            </Card>
          ) : runDetailQuery.isLoading ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Loading run details...
              </CardContent>
            </Card>
          ) : runDetailQuery.isError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{extractErrorMessage(runDetailQuery.error, 'Failed to load run')}</AlertDescription>
            </Alert>
          ) : !selectedRun ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Run not found.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={getStatusVariant(selectedRun.status)} className="gap-1">
                          {getStatusIcon(selectedRun.status)}
                          {STATUS_LABELS[selectedRun.status]}
                        </Badge>
                        {isActiveStatus(selectedRun.status) ? (
                          <Badge variant={isStreamConnected ? 'default' : 'outline'}>
                            {isStreamConnected ? 'Live Updates: Connected' : 'Live Updates: Reconnecting'}
                          </Badge>
                        ) : null}
                        <span className="text-sm text-muted-foreground">Run ID: {selectedRun.id}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{selectedRun.raw_query}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isActiveStatus(selectedRun.status) ? (
                        <Button
                          variant="outline"
                          onClick={() => cancelRunMutation.mutate(selectedRun.id)}
                          disabled={cancelRunMutation.isPending}
                        >
                          <CirclePause className="h-4 w-4 mr-2" />
                          Cancel Run
                        </Button>
                      ) : null}
                      {canExpandSearch ? (
                        <Button
                          variant="outline"
                          onClick={handleExpandSearch}
                          disabled={createRunMutation.isPending}
                        >
                          {createRunMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                          )}
                          Expand Search
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {selectedRun.enhanced_query && (
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Enhanced Query
                      </p>
                      <p className="text-sm text-foreground">{selectedRun.enhanced_query}</p>
                    </div>
                  )}

                  {!!selectedRun.execution_terms.length && (
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Execution Terms
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedRun.execution_terms.map((term) => (
                          <Badge key={term} variant="outline">
                            {term}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedRun.error && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{selectedRun.error}</AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Source Execution</h3>
                    <span className="text-xs text-muted-foreground">
                      {sortedSources.length} source{sortedSources.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {runDetailQuery.isLoading ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                            Loading sources...
                          </TableCell>
                        </TableRow>
                      ) : sortedSources.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                            No source rows yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedSources.map((source) => {
                          const sourceState = getSourceRowState(source)
                          const isRunningSourceForFullRun =
                            sourceState === 'running' && isActiveStatus(selectedRun.status)
                          const isRunningSingleSource =
                            sourceState === 'running' && !isActiveStatus(selectedRun.status)
                          const queuedSuggested = source.execution_status === 'queued_suggested'
                          const skipReasonLabel = getSourceSkipReasonLabel(source)
                          const canRunSource =
                            !isActiveStatus(selectedRun.status) &&
                            (source.execution_status === 'skipped' ||
                              source.execution_status === 'queued_suggested') &&
                            sourceState !== 'running'

                          return (
                            <TableRow key={source.id}>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium">{source.source_name || '-'}</span>
                                  {source.source_url ? (
                                    <a
                                      href={source.source_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary hover:underline"
                                    >
                                      {source.source_url}
                                    </a>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>
                                {isRunningSourceForFullRun || isRunningSingleSource ? (
                                  <Badge variant="secondary" className="gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Running
                                  </Badge>
                                ) : queuedSuggested ? (
                                  <Badge variant="secondary">Queued as Suggested</Badge>
                                ) : source.execution_status === 'executed' ? (
                                  <Badge>Executed</Badge>
                                ) : (
                                  <div className="space-y-1">
                                    <Badge variant="outline">Skipped</Badge>
                                    {skipReasonLabel ? (
                                      <p className="text-xs text-muted-foreground">{skipReasonLabel}</p>
                                    ) : null}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {canRunSource ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSourceToExecute(source)}
                                    disabled={executeSourceMutation.isPending}
                                  >
                                    Run Source
                                  </Button>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Matched Companies</h3>
                    <span className="text-xs text-muted-foreground">
                      {companyTotal} compan{companyTotal === 1 ? 'y' : 'ies'}
                    </span>
                  </div>

                  <Tabs
                    value={activeCompanyStage}
                    onValueChange={(v) =>
                      setActiveCompanyStage(v as PipelineStage | 'all')
                    }
                  >
                    <TabsList className="h-auto flex-wrap gap-1 bg-transparent p-0 mb-3">
                      <TabsTrigger
                        value="all"
                        className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-3 text-xs"
                      >
                        All
                        <Badge variant="secondary" className="ml-1.5 h-4 min-w-[18px] px-1">
                          {totalCompanies}
                        </Badge>
                      </TabsTrigger>
                      {PIPELINE_STAGES.map((stage) => {
                        const config = STAGE_CONFIG[stage]
                        const Icon = config.icon
                        const count =
                          stageCountsSum > 0
                            ? (stageCounts?.[stage] ?? 0)
                            : stage === 'sourced'
                              ? companyTotal
                              : 0
                        return (
                          <TabsTrigger
                            key={stage}
                            value={stage}
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-3 text-xs"
                          >
                            <Icon className="mr-1 h-3 w-3" />
                            {config.label}
                            <Badge variant="secondary" className="ml-1.5 h-4 min-w-[18px] px-1">
                              {count}
                            </Badge>
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>
                  </Tabs>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search companies..."
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        className="pl-9 h-9"
                      />
                    </div>
                    <Select
                      value={companySourceFilter}
                      onValueChange={setCompanySourceFilter}
                    >
                      <SelectTrigger className="w-[200px] h-9">
                        <SelectValue placeholder="Filter by source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          All sources
                          {companySources.length > 0 &&
                            ` (${companySources.reduce((s, x) => s + x.count, 0)})`}
                        </SelectItem>
                        {companySources.map((src) => {
                          const label =
                            src.source_name === '__unknown__'
                              ? '(Unknown)'
                              : src.source_name
                          return (
                            <SelectItem key={src.source_name} value={src.source_name}>
                              {label} ({src.count})
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Select all"
                          />
                        </TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Pipeline status</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companiesQuery.isLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                            Loading companies...
                          </TableCell>
                        </TableRow>
                      ) : companies.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                            No matched companies yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        companies.map((row) => {
                          const industry = (row.industry || '').trim()
                          const canonicalType = (row.company_type || '').trim()
                          const businessType = (row.business_type || '').trim()
                          const businessTypeIsPlaceholder = isPlaceholderBusinessType(businessType)
                          const normalizedBusinessType =
                            businessTypeIsPlaceholder || !businessType ? '' : businessType
                          const hasSpecificCanonicalType =
                            Boolean(canonicalType) && !isPlaceholderBusinessType(canonicalType)
                          const normalizedTypeTags = (row.company_type_tags || [])
                            .map((tag) => tag.trim())
                            .filter((tag) => tag && !isPlaceholderBusinessType(tag))
                          const primaryType =
                            industry ||
                            (hasSpecificCanonicalType
                              ? canonicalType
                              : normalizedBusinessType || (hasSpecificCanonicalType ? canonicalType : '')) ||
                            normalizedTypeTags[0] ||
                            ''
                          const showAnalyzing =
                            !primaryType && Boolean(selectedRunStatus && isActiveStatus(selectedRunStatus))
                          const showClassifiedAsOther =
                            canonicalType === 'other' &&
                            Boolean(normalizedBusinessType) &&
                            primaryType.toLowerCase() === normalizedBusinessType.toLowerCase()
                          const secondaryTypeTags = normalizedTypeTags
                            .filter((tag) => tag.toLowerCase() !== primaryType.toLowerCase() && !isPlaceholderBusinessType(tag))
                            .slice(0, 2)

                          const isExcluded = excludedCompanies.has(row.raw_company_id)

                          return (
                            <TableRow
                              key={row.id}
                              className={cn(isExcluded && "opacity-50")}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedIds.has(row.raw_company_id)}
                                  onCheckedChange={() => toggleCompany(row.raw_company_id)}
                                  aria-label={`Select ${row.company_name || 'company'}`}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (isExcluded) {
                                        includeMutation.mutate(row.raw_company_id)
                                      }
                                    }}
                                    disabled={!isExcluded || includeMutation.isPending}
                                    className={cn(
                                      "p-1 rounded hover:bg-muted transition-colors",
                                      isExcluded ? "text-muted-foreground hover:text-green-600" : "text-green-600"
                                    )}
                                    title={isExcluded ? "Include company" : "Company included"}
                                  >
                                    <ThumbsUp className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!isExcluded) {
                                        excludeMutation.mutate(row.raw_company_id)
                                      }
                                    }}
                                    disabled={isExcluded || excludeMutation.isPending}
                                    className={cn(
                                      "p-1 rounded hover:bg-muted transition-colors",
                                      isExcluded ? "text-red-600" : "text-muted-foreground hover:text-red-600"
                                    )}
                                    title={isExcluded ? "Company excluded" : "Exclude company"}
                                  >
                                    <ThumbsDown className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedCompanyForCampaign(row.raw_company_id)
                                      setCampaignDialogOpen(true)
                                    }}
                                    className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
                                    title="Add to Campaign"
                                  >
                                    <Megaphone className="h-4 w-4" />
                                  </button>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <Link
                                    to={`/app/companies/${row.raw_company_id}`}
                                    className="font-medium text-primary hover:underline"
                                  >
                                    {row.company_name || row.raw_company_id}
                                  </Link>
                                  {row.website ? (
                                    <a
                                      href={row.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-muted-foreground hover:underline"
                                    >
                                      {row.website}
                                    </a>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>
                                {showAnalyzing ? (
                                  <span className="text-sm font-medium text-muted-foreground">
                                    Analyzing...
                                  </span>
                                ) : primaryType ? (
                                  <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium">{primaryType}</span>
                                    {showClassifiedAsOther ? (
                                      <span className="text-xs text-muted-foreground">
                                        Classified as other
                                      </span>
                                    ) : secondaryTypeTags.length > 0 ? (
                                      <span className="text-xs text-muted-foreground">
                                        {secondaryTypeTags.join(', ')}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge className={getCompanyBadgeStyle(row.match_status)}>
                                  {row.match_status === 'added_to_pipeline'
                                    ? 'Added to Pipeline'
                                    : 'Already in Pipeline'}
                                </Badge>
                              </TableCell>
                              <TableCell>{row.source_name || row.company_source || '-'}</TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>

                  {companyTotal > 20 && (
                    <div className="flex items-center justify-between border-t pt-3">
                      <p className="text-sm text-muted-foreground">
                        Page {companyPage} of {Math.ceil(companyTotal / 20)}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCompanyPage((page) => Math.max(1, page - 1))}
                          disabled={companyPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCompanyPage((page) => page + 1)}
                          disabled={companyPage * 20 >= companyTotal}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
      </div>

      <SelectCampaignDialog
        open={campaignDialogOpen}
        onOpenChange={setCampaignDialogOpen}
        companyIds={
          selectedIds.size > 0
            ? Array.from(selectedIds)
            : selectedCompanyForCampaign
              ? [selectedCompanyForCampaign]
              : []
        }
        onSuccess={() => setSelectedIds(new Set())}
      />

      <BulkPipelineToolbar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds(new Set())}
        invalidateQueries={[
          ['companies'],
          ...(selectedRunId
            ? [
                ['lists', 'runs', selectedRunId, 'companies'],
                ['lists', 'runs', selectedRunId, 'companies', 'stage-counts'],
              ]
            : []),
        ]}
      >
        <BulkAddToCampaignButton
          selectedIds={selectedIds}
          onSelect={() => setCampaignDialogOpen(true)}
        />
      </BulkPipelineToolbar>

      <AlertDialog open={!!sourceToExecute} onOpenChange={(open) => !open && setSourceToExecute(null)}>
        <AlertDialogContent>
          {sourceToExecute && (() => {
            const dialogContent = getSourceExecuteDialogContent(sourceToExecute)
            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>{dialogContent.title}</AlertDialogTitle>
                  <AlertDialogDescription>{dialogContent.description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      if (selectedRunId && sourceToExecute) {
                        executeSourceMutation.mutate({
                          runId: selectedRunId,
                          sourceRowId: sourceToExecute.id,
                        })
                      }
                    }}
                    disabled={executeSourceMutation.isPending}
                  >
                    {executeSourceMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Running...
                      </>
                    ) : (
                      'Run Source'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            )
          })()}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
